import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { getAllStaff } from "../../../../../services/staff";
import { saveDigestSettings } from "../../../../../services/digest";
import SmsConsentManager from "../../../../../components/SmsConsentManager";
import "../settings.css";

/* ──────────────────────────────────────────────────────────────
   Notifications panel (redesign)

   Mirrors the live (staging) Setting/Notifications/index.jsx EXACTLY —
   nothing extra is invented. Staging has two tabs:
     1. Daily Digest  — digestEnabled / digestFrequency / digestSections
        -> saveDigestSettings (PUT /digest/settings), seeded from
        profile.user.*; recipients from getAllStaff("withAdmin"); live
        email preview in an iframe.
     2. Communications — <SmsConsentManager/> (SMS consent).

   (Delivery time / timezone are commented out on staging, so they are
   intentionally not rendered here.)
   ────────────────────────────────────────────────────────────── */

const SECTIONS = [
  {
    key: "staffLogin",
    title: "Staff login summary",
    desc: "Who logged in yesterday, with expected staff flagged if missing.",
    example: "6 of 8 staff logged in · 2 missed",
    badge: null,
  },
  {
    key: "staffDocs",
    title: "Staff documentation activity",
    desc: "Per-staff counts for Notes, ADL tasks and MAR entries.",
    example: "14 notes · 62 ADLs · 38 MAR",
    badge: null,
  },
  {
    key: "missingDocs",
    title: "Missing documentation alerts",
    desc: "Clients flagged by Command Center rules, included only when gaps exist.",
    example: "3 clients · 5 gaps",
    badge: "Requires Command Center",
  },
];

/* Live Daily Digest preview HTML (ported from Notifications/index.jsx). */
const buildDigestPreviewHTML = ({ staffLogin, staffDocs, missingDocs }) => `<!DOCTYPE html>
<html lang="en"><head><meta charset="UTF-8"/>
<meta name="viewport" content="width=device-width,initial-scale=1.0"/>
<title>Daily Digest Preview</title>
<style>body{margin:0;padding:16px;background:#fff;font-family:Inter,Arial,sans-serif;}*{box-sizing:border-box;}</style>
</head><body>
<table width="100%" cellpadding="0" cellspacing="0" border="0" style="max-width:640px;margin:0 auto;background:#fff;border-radius:16px;border:1px solid #E5E5E5;overflow:hidden;">
  <tr><td style="padding:21px 32px;border-bottom:1px solid #F2F2F2;">
    <table width="100%"><tr>
      <td><span style="font-size:18px;font-weight:800;color:#171717;">Ogiso</span></td>
      <td align="right"><span style="display:inline-block;width:6px;height:6px;background:#FFBA45;border-radius:3px;vertical-align:middle;margin-right:6px;"></span><span style="font-size:12px;font-weight:500;color:#737373;">Daily Digest</span></td>
    </tr></table></td></tr>
  <tr><td style="padding:28px 33px 20px;">
    <div style="font-size:12px;font-weight:600;color:#737373;text-transform:uppercase;letter-spacing:0.72px;margin-bottom:8px;">Yesterday at a glance</div>
    <div style="font-size:28px;font-weight:700;color:#171717;line-height:1.2;margin-bottom:10px;">Good morning.</div>
    <div style="font-size:15px;color:#525252;line-height:1.55;">Here&rsquo;s what happened across your organization on <strong style="color:#171717;">Thursday, May 10</strong>.</div>
  </td></tr>
  ${
    missingDocs
      ? `<tr><td style="padding:0 33px 20px;"><div style="background:#FEF3F2;border:1px solid #FECDCA;border-radius:10px;padding:13px 15px;"><div style="font-size:12.5px;font-weight:700;color:#7A271A;">3 clients &middot; 5 gaps total</div><div style="font-size:12.5px;color:#B42318;margin-top:4px;">Flagged by your Command Center rules.</div></div></td></tr>`
      : ""
  }
  ${
    staffLogin
      ? `<tr><td style="padding:0 33px 8px;"><div style="font-size:18px;font-weight:700;color:#171717;margin-bottom:2px;">Staff login activity</div><div style="font-size:13px;color:#737373;margin-bottom:14px;">9 of 11 expected staff logged in</div></td></tr>`
      : ""
  }
  ${
    staffDocs
      ? `<tr><td style="padding:8px 33px 8px;"><div style="font-size:18px;font-weight:700;color:#171717;margin-bottom:2px;">Documentation activity</div><div style="font-size:13px;color:#737373;margin-bottom:14px;">14 notes &middot; 62 ADLs &middot; 38 MAR entries</div></td></tr>`
      : ""
  }
  <tr><td style="padding:24px 33px 16px;"><div style="background:#171717;border-radius:12px;padding:20px 22px;"><div style="font-size:14px;font-weight:600;color:#fff;">Open the full report in Ogiso</div></div></td></tr>
  <tr><td style="padding:16px 33px 20px;border-top:1px solid #F2F2F2;background:#FAFAFA;"><span style="font-size:12px;color:#737373;">Sent to Admins of your organization.</span></td></tr>
</table></body></html>`;

export default function NotificationsPanel({ profile, role }) {
  const allStaff = useSelector((state) => state?.staff?.allStaff);
  const reduxProfile = useSelector((state) => state?.profile?.profile);
  const userProfile = profile || reduxProfile;

  const adminRecipients = (allStaff || []).filter(
    (s) => s?.type === "staff" && s?.role === "admin"
  );

  const [activeTab, setActiveTab] = useState("digest");
  const [previewOpen, setPreviewOpen] = useState(false);

  // ── Daily Digest state (wired to saveDigestSettings) ──
  const [subscribed, setSubscribed] = useState(false);
  const [frequency, setFrequency] = useState("daily");
  const [checkedSections, setCheckedSections] = useState({
    staffLogin: true,
    staffDocs: true,
    missingDocs: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!allStaff?.length) getAllStaff("withAdmin");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!userProfile) return;
    setSubscribed(userProfile?.user?.digestEnabled ?? false);
    if (userProfile?.user?.digestFrequency) {
      setFrequency(userProfile.user.digestFrequency);
    }
    const sections = userProfile?.user?.digestSections;
    if (sections) {
      setCheckedSections({
        staffLogin: sections.staffLogin ?? true,
        staffDocs: sections.staffDocs ?? true,
        missingDocs: sections.missingDocs ?? true,
      });
    }
  }, [userProfile]);

  const toggleSection = (key) =>
    setCheckedSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSaveDigest = async () => {
    setSaving(true);
    await saveDigestSettings({
      digestEnabled: subscribed,
      digestFrequency: frequency,
      digestSections: checkedSections,
    });
    setSaving(false);
  };

  return (
    <div className="settings-panel active" data-panel="notifications">
      <div className="page-header">
        <div>
          <div className="page-title">Notifications</div>
          <div className="page-subtitle">
            Control how Ogiso reaches you and your team.
          </div>
        </div>
      </div>

      {/* Sub-tabs — match staging: Daily Digest + Communications */}
      <div className="tabs">
        <button
          type="button"
          className={`tab${activeTab === "digest" ? " active" : ""}`}
          onClick={() => setActiveTab("digest")}
        >
          Daily Digest
        </button>
        <button
          type="button"
          className={`tab${activeTab === "comms" ? " active" : ""}`}
          onClick={() => setActiveTab("comms")}
        >
          Communications
        </button>
      </div>

      {/* ───── Daily Digest (wired to saveDigestSettings) ───── */}
      {activeTab === "digest" && (
        <div className="subpanel" data-subpanel="digest">
          <div className="card">
            {/* Header */}
            <div
              className="card-section"
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 16,
              }}
            >
              <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 10,
                    background: "var(--app-tint-amber)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--app-ink-amber)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
                    <path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" />
                  </svg>
                </div>
                <div>
                  <div className="card-title">Daily Digest</div>
                  <div className="card-subtitle" style={{ maxWidth: 540 }}>
                    A once-daily email summarizing staff logins, documentation
                    activity and compliance gaps from the previous day. Sent to
                    all Admins.
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn"
                onClick={() => setPreviewOpen(true)}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--app-text-muted)"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
                Preview email
              </button>
            </div>

            {/* Subscribe toggle */}
            <div className="row">
              <div className="row-text">
                <div className="row-title">Send me the Daily Digest</div>
                <div className="row-subtitle">
                  You&apos;ll receive your digest by 7:00 AM each morning.
                </div>
              </div>
              <button
                type="button"
                className={`switch${subscribed ? " on" : ""}`}
                onClick={() => setSubscribed((v) => !v)}
                aria-pressed={subscribed}
              />
            </div>

            {/* Frequency */}
            <div
              className="row"
              style={subscribed ? undefined : { opacity: 0.5, pointerEvents: "none" }}
            >
              <div className="row-text">
                <div className="row-title">Frequency</div>
                <div className="row-subtitle">
                  Weekdays only if your facility doesn&apos;t operate on weekends.
                </div>
              </div>
              <div className="segmented">
                <button
                  type="button"
                  className={frequency === "daily" ? "active" : ""}
                  onClick={() => setFrequency("daily")}
                >
                  Every day
                </button>
                <button
                  type="button"
                  className={frequency === "weekdays" ? "active" : ""}
                  onClick={() => setFrequency("weekdays")}
                >
                  Weekdays only
                </button>
              </div>
            </div>

            {/* Sections — wired to digestSections */}
            <div
              className="card-section"
              style={subscribed ? undefined : { opacity: 0.5, pointerEvents: "none" }}
            >
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--muted)",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  marginBottom: 4,
                }}
              >
                Sections
              </div>
              <div style={{ fontSize: 13.5, color: "var(--app-text-muted)", marginBottom: 16 }}>
                Choose which sections appear in your email.
              </div>
              {SECTIONS.map((s) => {
                const checked = checkedSections[s.key];
                return (
                  <div
                    className={`sec-toggle${checked ? " checked" : ""}`}
                    key={s.key}
                  >
                    <button
                      type="button"
                      className={`checkbox${checked ? " on" : ""}`}
                      onClick={() => toggleSection(s.key)}
                      aria-pressed={checked}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div className="sec-title">{s.title}</div>
                        {s.badge && (
                          <span className="badge badge-info">{s.badge}</span>
                        )}
                      </div>
                      <div className="sec-desc">{s.desc}</div>
                      <div className="sec-example">
                        Example: <strong>{s.example}</strong>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Recipients — real admins from getAllStaff("withAdmin") */}
            <div className="card-section">
              <div>
                <div className="row-title">Recipients</div>
                <div className="row-subtitle" style={{ marginTop: 2 }}>
                  All users with the{" "}
                  <strong style={{ color: "var(--app-text)" }}>Administrator</strong> role
                  receive this digest.
                </div>
              </div>
              <div
                style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}
              >
                {adminRecipients.length === 0 && (
                  <span className="row-subtitle">No admins found.</span>
                )}
                {adminRecipients.map((r) => {
                  const displayName =
                    r.name ||
                    `${r.firstName || ""} ${r.lastName || ""}`.trim() ||
                    "Admin";
                  const initial = (displayName[0] || "A").toUpperCase();
                  return (
                    <div className="chip" key={r._id || displayName}>
                      <div className="chip-avatar">{initial}</div>
                      {displayName}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="card-footer">
              <button
                type="button"
                className="btn primary"
                onClick={handleSaveDigest}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ───── Communications (SmsConsentManager — fully wired) ───── */}
      {activeTab === "comms" && (
        <div className="subpanel" data-subpanel="comms">
          <SmsConsentManager />
        </div>
      )}

      {/* ───── Daily Digest preview modal ───── */}
      <div className={`modal-overlay${previewOpen ? " open" : ""}`} style={{ zIndex: 1000 }}>
        <div className="modal-frame">
          <div className="modal-head">
            <div className="modal-head-title">Daily Digest Preview</div>
            <button
              type="button"
              className="modal-close"
              onClick={() => setPreviewOpen(false)}
              aria-label="Close preview"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--app-text-muted)"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="modal-body" style={{ padding: 16 }}>
            {previewOpen && (
              <iframe
                title="Daily Digest Preview"
                srcDoc={buildDigestPreviewHTML(checkedSections)}
                sandbox="allow-same-origin"
                style={{
                  width: "100%",
                  height: 520,
                  border: "1px solid var(--border)",
                  borderRadius: 12,
                  background: "var(--app-surface)",
                }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
